import { ChangeEvent, ClipboardEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { redirect, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from 'flowbite-react';
import { useTimeout } from 'webrix/hooks';
import OtpInput from '../shared/OtpInput';
import { useMutation } from 'react-query';
import { resolveOtpSessionId, sendOtp, verifyOtp } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { useGetPhoneVerificationStatus } from 'src/hooks/user.hooks';
import { useUserContext } from 'src/context/UserProvider';
import { get } from 'lodash';
import { sendTrackingEvent } from 'src/utils/gtm';
import { isRecruiterSide } from 'src/config/env.config';
import { sendMixpanelTrackingEvent } from 'src/utils/mixpanel';

interface Props {
	emailORPhone: string;
	btnText?: string;
	isSigningIn?: boolean;
	phoneOrEmail?: boolean;
	setPhoneOrEmail?: (phoneOrEmail: boolean) => void;
}

const PhoneVerification = ({ emailORPhone, btnText, isSigningIn, phoneOrEmail, setPhoneOrEmail }: Props) => {
	const location = useLocation();
	const { user } = useUserContext();
	const navigate = useNavigate();
	const toast = useToast();
	const { search } = useLocation();
	const [searchParamas] = useSearchParams();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [otp, setOtp] = useState(['', '', '', '', '', '']);
	const [time, setTime] = useState(30);
	const [isResendEnabled, setIsResendEnabled] = useState(false);
	const [isMobileScreen, setIsMobileScreen] = useState(false);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const redirectUrl = searchParamas.get('redirect_url');

	const { start, stop } = useTimeout(() => {
		if (time > 0) {
			setTime(time - 1);
		}
	}, 1000);

	useEffect(() => {
		if (time <= 0) {
			setIsResendEnabled(true);
			stop();
		}
	}, [stop, time]);

	useEffect(() => {
		start();
	}, [start]);

	const phoneVerificationStatus = useGetPhoneVerificationStatus(emailORPhone);

	const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
		const newOtp = [...otp];
		newOtp[index] = event.target.value;
		setOtp(newOtp);
		if (event.target.value && index < otp.length - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Backspace' && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const pasteData = event.clipboardData.getData('text/plain').slice(0, 6);
		const newOtp = [...otp];
		pasteData?.split('').forEach((digit, index) => {
			newOtp[index] = digit;
		});
		setOtp(newOtp);
	};

	const verifyPhoneFn = useMutation({
		mutationKey: 'verifyOtp',
		mutationFn: verifyOtp,
		onSuccess() {
			if (redirectUrl?.includes('placement-drive-registration')) {
				sendMixpanelTrackingEvent('signup_done_interview_placement_drive', {
					source: 'Placed_Signup_Verify_OTP',
					location: window.location.href,
					userId: user?._id ?? localStorage.getItem('userId') ?? '',
				});
				sendTrackingEvent({
					event: 'signup_done_interview_placement_drive',
					eventLabel: location.pathname + location.search,
					userId: user?._id ?? localStorage.getItem('userId') ?? '',
				});
			}
			// clear userId info into localstorage
			localStorage.removeItem('userId');
			toast({
				type: 'success',
				title: `${isRecruiterSide ? 'Email ' : 'Phone '}verified successfully.`,
			});
			setIsLoading(false);
			phoneVerificationStatus.refetch();
			localStorage.removeItem('pageUrl');
			window.open(decodeURIComponent((search.split('=')[1] as string) ?? '/dashboard${search}'), '_self');
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
		},
	});

	const generateVerificationCodeFn = useMutation({
		mutationKey: 'sendOtp',
		mutationFn: sendOtp,
		onSuccess() {
			toast({
				type: 'success',
				title: `An OTP has sent to your registered ${isRecruiterSide ? ' Email' : ' Phone'}.`,
			});
			setIsResendEnabled(false);
			setIsLoading(false);
			phoneVerificationStatus.refetch();
		},
		onError(err: Error) {
			const errorMessage = get(err, ['response', 'data', 'message'], err.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
		},
	});

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const otpSessionId = await resolveOtpSessionId(
			emailORPhone,
			phoneVerificationStatus.data?.verification?.otpSessionId,
		);
		if (otpSessionId) {
			if (isRecruiterSide) {
				sendTrackingEvent({
					event: 'recr_verify',
					eventLabel: location.pathname,
				});
			} else {
				sendTrackingEvent({
					event: 'verify_code',
					eventLabel: location.pathname,
				});
			}
			setIsLoading(true);
			const payload = {
				emailOrPhone: emailORPhone,
				otp: otp.join(''),
				otpSessionId,
			};
			verifyPhoneFn.mutate(payload);
		}
	};

	useEffect(() => {
		setIsMobileScreen(window.innerWidth <= 960);
	}, []);

	useEffect(() => {
		if (user?.role) {
			navigate(`/dashboard${search}`);
		}
	});

	return (
		<div className="sm:w-full sm:max-w-lg px-4 py-3">
			<form className={'flex flex-col gap-4 shrink-0'} onSubmit={handleSubmit}>
				<div className="dark:text-white text-[#1C1C28] md:w-[358px]">
					{!isSigningIn && (
						<p className="flex items-center justify-start gap-2 font-inter text-xl font-bold">
							Please Verify Your {phoneOrEmail ? 'Email' : 'Phone'}
						</p>
					)}

					<div className="mt-4">
						<span className="email_verification_para_text font-inter">{`Check the code sent to `}</span>
						<span className="dark:text-[#CDF] text-[#7777E7] email_verification_para_text font-inter">
							{emailORPhone?.trim() || ''}{' '}
						</span>
						<span className="email_verification_para_text font-inter">{`and enter it below.`}</span>
						<span
							className="cursor-pointer ml-2 dark:text-[#6698FA] text-[#7777E7] text-sm underline"
							onClick={() => {
								setPhoneOrEmail && setPhoneOrEmail(true);
							}}
						>
							{`Change ${isRecruiterSide ? ' Email' : ' Phone'}`}
						</span>
					</div>
				</div>
				<div className="md:w-[358px]">
					<OtpInput
						isMobileScreen={isMobileScreen}
						handlePaste={handlePaste}
						handleKeyDown={handleKeyDown}
						handleChange={handleChange}
						otp={otp}
						inputRefs={inputRefs}
					/>

					<div className="mt-[4px]">
						{time > 0 && !isResendEnabled ? (
							<div className="cursor-not-allowed">
								<span className="text-sm dark:text-[#3F83F8] text-[#7777E7]">Resend code in </span>
								<span className="dark:text-[#C7C9D9] text-[#555770]">{time} sec</span>
							</div>
						) : (
							<span
								className="cursor-pointer text-sm text-[#3F83F8]"
								onClick={() => {
									if (isRecruiterSide) {
										sendTrackingEvent({
											event: 'recr_resend_code',
											eventLabel: location.pathname,
										});
									} else {
										sendTrackingEvent({
											event: 'resend_code',
											eventLabel: location.pathname,
										});
									}

									if (isResendEnabled) {
										setOtp(['', '', '', '', '', '']);
										generateVerificationCodeFn.mutate({ emailOrPhone: emailORPhone });
										setTime(30);
										start();
									}
								}}
							>
								Resend Code
							</span>
						)}
					</div>
				</div>

				<div className="max-w-[332px] md:pb-14 md:mt-4">
					<Button
						color="purplePlaced"
						disabled={isLoading || otp.join('').length < 6 || !phoneVerificationStatus.data?.verification}
						type="submit"
						className={' w-[100%] bg-[#7777E7]'}
					>
						{isLoading ? <Spinner /> : btnText || 'Verify'}
					</Button>
				</div>
			</form>
		</div>
	);
};

export default PhoneVerification;
